
import { useEffect, useState } from 'react';

interface FinalResultModalProps {
  /** Control modal visibility */
  show: boolean;
  /** Name of the winner */
  winner: string;
  /** Drink name */
  drink: string;
  /** Emoji representing the drink */
  drinkEmoji: string;
  /** Callback when the modal should be closed */
  onClose: () => void;
}

/**
 * FinalResultModal
 *
 * Displays the final result of the game with a single **Close** action.
 * The component handles the show/hide transition internally.
 */
export default function FinalResultModal({
  show,
  winner,
  drink,
  drinkEmoji,
  onClose,
}: FinalResultModalProps) {
  const [isVisible, setIsVisible] = useState(false);

  // Manage entry/exit animation timing
  useEffect(() => {
    if (show) {
      setIsVisible(true);
    } else {
      const timeout = setTimeout(() => setIsVisible(false), 300);
      return () => clearTimeout(timeout);
    }
  }, [show]);

  // Do not render anything when the modal is not visible
  if (!isVisible) return null;

  return (
    <div
      className={`fixed inset-0 z-50 flex items-center justify-center p-4 transition-all duration-300 ${
        show ? 'opacity-100' : 'opacity-0 pointer-events-none'
      }`}
    >
      {/* Background overlay – click closes the modal */}
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />

      {/* Modal container */}
      <div
        className={`relative bg-white rounded-3xl shadow-2xl w-full max-w-md mx-auto p-6 sm:p-8 transform transition-all duration-300 max-h-[90vh] overflow-y-auto ${
          show ? 'scale-100' : 'scale-90'
        }`}
      >
        {/* Close (X) icon */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-colors cursor-pointer"
          aria-label="Close modal"
        >
          <i className="ri-close-line text-lg" />
        </button>

        <div className="text-center">
          {/* Celebration icon */}
          <div className="w-20 h-20 bg-gradient-to-br from-yellow-400 to-orange-500 rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
            <span className="text-4xl">🎉</span>
          </div>

          <h2 className="text-2xl font-extrabold text-gray-900 mb-4">Final Result!</h2>

          {/* Winner & drink info */}
          <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl p-6 mb-6">
            <p className="text-2xl font-extrabold text-[#2349FF] mb-2">{winner}</p>
            <p className="text-sm text-gray-600">is buying</p>
            <p className="text-3xl font-extrabold text-gray-900 mt-1">
              {drinkEmoji} {drink}
            </p>
          </div>

          <p className="text-sm text-gray-500 mb-6">
            No excuses! Time to head to the bar! 🎉
          </p>

          {/* Primary action – full‑width Close button */}
          <button
            onClick={onClose}
            className="w-full px-5 py-4 bg-gradient-to-r from-[#2349FF] to-[#1a38cc] text-white text-base font-bold rounded-full hover:from-[#1a38cc] hover:to-[#1530b3] transition-all active:scale-95"
          >
            <i className="ri-close-line mr-1.5" />Close
          </button>
        </div>
      </div>
    </div>
  );
}
